import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { usePredictions } from "./PredictionsContext";
import styles from "./Predictions.module.css";
import { useTranslation } from "react-i18next";

function PredictionForm({ match, onSubmitted }) {
  const [homeGoals, setHomeGoals] = useState(0);
  const [awayGoals, setAwayGoals] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { addPrediction } = usePredictions();
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    if (match?.prediction) {
      setHomeGoals(match.prediction.home_score ?? 0);
      setAwayGoals(match.prediction.away_score ?? 0);
    }
  }, [match]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("home_score", homeGoals);
      form.append("away_score", awayGoals);

      await axios.post(
        `https://admin.osleague.online/api/fixtures/${match.id}/predict`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const prediction = {
        type: "exact",
        home: homeGoals,
        away: awayGoals,
        label: `${homeGoals} - ${awayGoals}`,
      };
      addPrediction(match, prediction);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || t("prediction.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.successScreen}>
        <div className={styles.successIcon}>✓</div>
        <h2 className={styles.successTitle}>{t("prediction.successTitle")}</h2>

        <p className={styles.successSub}>
          {t("prediction.youPredicted")}{" "}
          <strong>
            {match.home.name} vs {match.away.name}
          </strong>
        </p>

        <button className={styles.backToPredBtn} onClick={onSubmitted}>
          {t("prediction.backHome")}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={styles.glowBar} />

      <div className={styles.banner}>
        <div className={styles.bannerTop}>
          <div className={styles.leagueName}>
            <span className={styles.leagueDot} />
            {match.league && (
              <>
                <img
                  src={match.leagueBadge}
                  alt={match.league}
                  style={{ width: 16, height: 16, marginRight: 4 }}
                />
                {match.league}
              </>
            )}
            {match.venue && (
              <span className={styles.venue}>· {match.venue}</span>
            )}
          </div>
          {match.status === "live" ? (
            <div className={styles.livePill}>
              <span className={styles.liveDot} />
              LIVE {match.liveTime}
            </div>
          ) : (
            <div className={styles.datePill}>{match.date}</div>
          )}
        </div>

        <div className={styles.teamsRow}>
          <div className={styles.teamHero}>
            <div className={styles.teamCrest}>
              <img
                src={match.home.logo}
                alt={match.home.name}
                className={styles.teamLogo}
              />
            </div>
            <span className={styles.teamHeroName}>
              {match.home.name.toUpperCase()}
            </span>
          </div>

          <div className={styles.vsDivider}>
            <div className={styles.vsLine} />
            <span>VS</span>
            <div className={styles.vsLine} />
          </div>

          <div className={styles.teamHero}>
            <div className={styles.teamCrest}>
              <img
                src={match.away.logo}
                alt={match.away.name}
                className={styles.teamLogo}
              />
            </div>
            <span className={styles.teamHeroName}>
              {match.away.name.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.predictionBox}>
        <h2 className={styles.predTitle}>{t("prediction.submitTitle")}</h2>
        <div className={styles.scoreInput}>
          <div className={styles.scoreTeam}>
            <label className={styles.scoreLabel}>
              {match.home.name.toUpperCase()} {t("prediction.homeGoals")}{" "}
            </label>
            <div className={styles.scoreControl}>
              <button
                className={styles.scoreBtn}
                onClick={() => setHomeGoals(Math.max(0, homeGoals - 1))}
              >
                −
              </button>
              <span className={styles.scoreValue}>{homeGoals}</span>
              <button
                className={styles.scoreBtn}
                onClick={() => setHomeGoals(homeGoals + 1)}
              >
                +
              </button>
            </div>
          </div>

          <div className={styles.colonSep}>:</div>

          <div className={styles.scoreTeam}>
            <label className={styles.scoreLabel}>
              {match.away.name.toUpperCase()} {t("prediction.awayGoals")}{" "}
            </label>
            <div className={styles.scoreControl}>
              <button
                className={styles.scoreBtn}
                onClick={() => setAwayGoals(Math.max(0, awayGoals - 1))}
              >
                −
              </button>
              <span className={styles.scoreValue}>{awayGoals}</span>
              <button
                className={styles.scoreBtn}
                onClick={() => setAwayGoals(awayGoals + 1)}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {error && (
          <p style={{ color: "red", textAlign: "center", marginTop: 8 }}>
            {error}
          </p>
        )}

        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? t("prediction.submitting") : t("prediction.submit")}{" "}
        </button>
      </div>
    </>
  );
}

function PredictionsList() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = useSelector((state) => state.auth.token);
  const { t } = useTranslation();
  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        const response = await axios.get(
          "https://admin.osleague.online/api/fixtures/active-rounds",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const matchesFromApi = response.data.data;

        const formatDate = (dateString) => {
          const options = {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          };
          return new Date(dateString)
            .toLocaleDateString("en-GB", options)
            .replace(",", " ·");
        };

        const predicted = matchesFromApi
          .filter((match) => match.prediction !== null)
          .map((match) => ({
            match: {
              id: match.id,
              date: formatDate(match.match_date),
              status: match.status,
              league: match.league.name,
              leagueBadge: match.league.logo ?? null,
              home: {
                name: match.home_team.name,
                logo: match.home_team.logo,
                score: match.home_team.score,
              },
              away: {
                name: match.away_team.name,
                logo: match.away_team.logo,
                score: match.away_team.score,
              },
            },
            prediction: {
              type: "exact",
              home: match.prediction.home_score,
              away: match.prediction.away_score,
              label: `${match.prediction.home_score} - ${match.prediction.away_score}`,
            },
            submittedAt: new Date().toISOString(),
          }));

        setPredictions(predicted);
      } catch (error) {
        console.error("Error loading predictions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [token]);

  if (loading)
    return <div className={styles.loader}>{t("common.loading")}</div>;

  if (predictions.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <h2 className={styles.emptyTitle}>{t("prediction.noMatches")}</h2>

          <p className={styles.emptySub}>{t("prediction.goHome")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.listHeader}>
        <h1 className={styles.listTitle}>{t("prediction.myPredictions")}</h1>{" "}
        <span className={styles.listCount}>
          {predictions.length} {t("prediction.matchesCount")}
        </span>
      </div>

      <div className={styles.predList}>
        {[...predictions].reverse().map((p, i) => (
          <div key={i} className={styles.predCard}>
            <div className={styles.predCardTop}>
              <span className={styles.predLeague}>
                {p.match.leagueBadge && (
                  <img
                    src={p.match.leagueBadge}
                    alt=""
                    style={{ width: 16, height: 16, marginRight: 4 }}
                  />
                )}
                {p.match.league}
              </span>
              <span className={styles.predDate}>{p.match.date}</span>
            </div>

            <div className={styles.predMatchRow}>
              <div className={styles.predTeam}>
                <img
                  src={p.match.home.logo}
                  alt={p.match.home.name}
                  className={styles.teamLogo}
                />
                <span className={styles.predTeamName}>{p.match.home.name}</span>
              </div>

              <div className={styles.predResultBadge}>
                <span className={styles.predResultLabel}>
                  {t("prediction.yourPick")}
                </span>{" "}
                <span className={styles.predResultValue}>
                  {p.prediction.label}
                </span>
              </div>

              <div className={styles.predTeam}>
                <img
                  src={p.match.away.logo}
                  alt={p.match.away.name}
                  className={styles.teamLogo}
                />
                <span className={styles.predTeamName}>{p.match.away.name}</span>
              </div>
            </div>

            <div className={styles.predCardBottom}>
              <span className={styles.predType}>
                {t("prediction.exactScore")}
              </span>{" "}
              <span className={styles.predSubmitted}>
                {t("prediction.submitted")}{" "}
                {new Date(p.submittedAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Predictions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMatch, setActiveMatch] = useState(location.state?.match || null);

  const handleSubmitted = () => {
    setActiveMatch(null);
    navigate("/dashboard", { replace: true });
  };

  if (activeMatch) {
    return (
      <div className={styles.page}>
        <PredictionForm match={activeMatch} onSubmitted={handleSubmitted} />
      </div>
    );
  }

  return <PredictionsList />;
}
